import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as navVisibilityApi from '../Api/navVisibilityPreferenceApi';

const NAV_VISIBILITY_KEY = 'navVisibilityPreferences';

export const useNavVisibilityPreferences = (companyId) =>
	useQuery({
		queryKey: [NAV_VISIBILITY_KEY, companyId],
		queryFn: () => navVisibilityApi.getNavVisibilityPreferences(companyId),
		enabled: !!companyId,
		select: (res) => res.data?.hiddenItemIds ?? [],
		staleTime: 1000 * 60 * 5,
	});

export const useUpdateNavVisibilityPreferences = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ companyId, hiddenItemIds }) =>
			navVisibilityApi.updateNavVisibilityPreferences(companyId, hiddenItemIds),
		onMutate: async ({ companyId, hiddenItemIds }) => {
			// Optimistic update — apply immediately in cache
			await queryClient.cancelQueries({ queryKey: [NAV_VISIBILITY_KEY, companyId] });
			const previous = queryClient.getQueryData([NAV_VISIBILITY_KEY, companyId]);
			queryClient.setQueryData([NAV_VISIBILITY_KEY, companyId], (old) => ({
				...(old || {}),
				data: { hiddenItemIds },
			}));
			return { previous, companyId };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous !== undefined) {
				queryClient.setQueryData(
					[NAV_VISIBILITY_KEY, context.companyId],
					context.previous,
				);
			}
		},
		onSettled: (_data, _err, { companyId }) => {
			queryClient.invalidateQueries({ queryKey: [NAV_VISIBILITY_KEY, companyId] });
		},
	});
};

export const useResetNavVisibilityPreferences = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (companyId) =>
			navVisibilityApi.resetNavVisibilityPreferences(companyId),
		onSuccess: (_data, companyId) => {
			queryClient.setQueryData([NAV_VISIBILITY_KEY, companyId], { data: { hiddenItemIds: [] } });
		},
	});
};
